import { computed, inject, Injectable } from '@angular/core';
import { createInfiniteScroll, type InfiniteScrollRefreshSource } from '@okcargo-apps/layout-ionic';
import { dedupeVehiclesByPlateNumber, IVehicle } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { VehicleQueryService } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../account';

@Injectable({
  providedIn: 'root',
})
export class VehiclesStoreService {
  private readonly vehicleQueryService = inject(VehicleQueryService);
  private readonly accountService = inject(AccountService);

  private readonly PAGE_SIZE = 200;
  private readonly RELOAD_DELAY_MS = 2000;

  /** Vehicles are loaded when the active user can manage or assign them. */
  private readonly canViewVehicles = computed(
    () =>
      this.accountService.hasCapability(ECapabilities.CRUD_VEHICLES) ||
      this.accountService.hasCapability(ECapabilities.INTERACT_WITH_STOPS),
  );

  // The backend does not return pagination metadata, so totalElements is hydrated by counting the
  // returned items. Ready to switch to real pagination once the query provides it.
  private readonly vehiclesCollection = createInfiniteScroll<{ list: string }, IVehicle>({
    query: computed(() => (this.canViewVehicles() ? { list: 'vehicles' } : null)),
    pageSize: this.PAGE_SIZE,
    loadPage: async (_query, pageIndex, pageSize, abortSignal) => {
      const items = await this.vehicleQueryService.searchVehicles({ pageIndex, pageSize, abortSignal });
      // The count must reflect the deduplicated collection, not the raw backend response.
      const vehicles = dedupeVehiclesByPlateNumber(items);
      return { items: vehicles, totalElements: vehicles.length };
    },
    getItemKey: (vehicle) => vehicle.id,
  });

  public readonly vehiclesVM = this.vehiclesCollection.vm;

  public loadNextVehiclesPage(): void {
    this.vehiclesCollection.loadNextPage();
  }

  public retryVehicles(): void {
    this.vehiclesCollection.retry();
  }

  public reloadVehicles(source: InfiniteScrollRefreshSource = 'polling'): void {
    this.vehiclesCollection.refresh(source);
  }

  /**
   * Consumer components (the vehicles list, the assignation modal) reload the vehicles when they
   * are initialized. This query is not refreshed by the general application refresh: it loads
   * once on application start (declaratively, keeping data for offline mode) and then on each
   * consumer component initialization.
   */
  public reloadVehiclesOnComponentInit(): void {
    this.reloadVehicles('user');
  }

  /** Reloads the vehicles after a short delay, once the backend has processed the command. */
  public async reloadAfterSaveData(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, this.RELOAD_DELAY_MS));
    this.reloadVehicles('user');
  }
}
